import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ROUTES, POS_ACCREDITATION_DETAILS } from 'utils/constants';
import {
  getPendingTransaction,
  storeCompletedOrder,
  CartItem,
} from 'utils/posIndexedDBUtils';
import { syncPendingTransaction, isOnline } from 'utils/posSyncService';
import { useSalesService } from 'services/sales';
import { useUserProfileStore } from 'stores/userProfileStore';
import { useCompanyServiceQueries } from 'services/company';
import { usePrinterStore } from 'stores/printerStore';
import { printReceipt, ReceiptData } from 'utils/printerUtils';
import { Company } from 'types/company';
import { Customer } from 'types/sales';
import styles from './Success.module.scss';

interface VatBreakdown {
  vatableSales?: number;
  vatExemptSales?: number;
  vatZeroRatedSales?: number;
  vatAmount?: number;
}

interface CompanySnapshot {
  name?: string;
  address?: string;
  tin?: string;
  vatStatus?: string;
  posSerialNumber?: string;
  machineIdentificationNumber?: string;
}

interface SuccessLocationState {
  saleId: string;
  total: number;
  change: number;
  amountPaid?: number;
  subtotal?: number;
  cart?: CartItem[];
  customer?: Customer | null;
  paymentMethodName?: string;
  transactionDate?: string;
  isLocal?: boolean;
  vatBreakdown?: VatBreakdown;
  companySnapshot?: CompanySnapshot;
}

const DISPLAY_PLACEHOLDER = 'Not provided';

function getDisplayValue(
  value?: string | number | null,
  formatter?: (val: string | number) => string,
  placeholder: string = DISPLAY_PLACEHOLDER,
) {
  if (
    value === null ||
    value === undefined ||
    (typeof value === 'string' && value.trim() === '')
  ) {
    return { text: placeholder, isPlaceholder: true };
  }
  if (typeof value === 'number' && Number.isNaN(value)) {
    return { text: placeholder, isPlaceholder: true };
  }
  return {
    text: formatter ? formatter(value) : String(value),
    isPlaceholder: false,
  };
}

function Success() {
  const navigate = useNavigate();
  const location = useLocation();
  const salesService = useSalesService();
  const profile = useUserProfileStore((s) => s.profile);
  const companyService = useCompanyServiceQueries();
  const state = location.state as SuccessLocationState | null;
  const [syncStatus, setSyncStatus] = useState<
    'syncing' | 'synced' | 'pending' | 'error'
  >('pending');
  const [online, setOnline] = useState(isOnline());
  const [companyDetails, setCompanyDetails] = useState<Company | null>(null);
  const [companyError, setCompanyError] = useState<string | null>(null);
  const [printError, setPrintError] = useState<string | null>(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const isSyncingRef = useRef(false); // Prevent multiple simultaneous sync attempts
  const hasAutoPrintedRef = useRef(false);

  const transactionDateValue = state?.transactionDate ?? null;
  const transactionTimestamp = useMemo(() => {
    if (!transactionDateValue) {
      return new Date();
    }
    const parsed = new Date(transactionDateValue);
    if (Number.isNaN(parsed.getTime())) {
      return new Date();
    }
    return parsed;
  }, [transactionDateValue]);

  const vatSnapshot = state?.vatBreakdown;
  const totalForVat = state?.total ?? 0;

  const savedPrinter = usePrinterStore((s) => s.savedPrinter);
  const activeDevice = usePrinterStore((s) => s.activeDevice);
  const setActiveDevice = usePrinterStore((s) => s.setActiveDevice);
  const printerSettings = usePrinterStore((s) => s.printerSettings);

  const cartItems = state?.cart ?? [];
  const subtotal = state?.subtotal ?? state?.total ?? 0;
  const cashAmount = state?.amountPaid ?? subtotal;
  const changeAmount =
    state?.change !== undefined
      ? state.change
      : Math.max(cashAmount - subtotal, 0);
  const customerInfo = state?.customer ?? null;
  const companySnapshot = state?.companySnapshot;

  const companyNameSource =
    companySnapshot?.name || companyDetails?.name || undefined;
  const companyAddressSource =
    companySnapshot?.address || companyDetails?.description || undefined;

  const cashierName = profile?.username || profile?.email || 'Cashier';

  const companyNameDisplay = getDisplayValue(
    companyNameSource,
    undefined,
    'Company name not provided',
  );
  const companyAddressDisplay = getDisplayValue(
    companyAddressSource,
    undefined,
    companyError ? 'Unable to load company address' : 'Address not provided',
  );
  const vatStatusRaw = companySnapshot?.vatStatus?.trim() ?? null;
  const vatStatusLabel = vatStatusRaw
    ? vatStatusRaw.toLowerCase().includes('non')
      ? 'NON-VAT'
      : vatStatusRaw.toLowerCase().includes('vat')
        ? 'VAT REG.'
        : vatStatusRaw.toUpperCase()
    : null;
  const shouldShowVatBreakdown = Boolean(vatStatusLabel);
  const tinDisplay = getDisplayValue(
    companySnapshot?.tin,
    undefined,
    'TIN not provided',
  );
  const posSerialDisplay = getDisplayValue(
    companySnapshot?.posSerialNumber,
    undefined,
    'POS serial pending registration',
  );
  const machineIdDisplay = getDisplayValue(
    companySnapshot?.machineIdentificationNumber,
    undefined,
    'Machine ID pending registration',
  );
  const cashierDisplay = getDisplayValue(
    cashierName,
    undefined,
    'Cashier name not provided',
  );
  const dateTimeDisplay = getDisplayValue(
    `${transactionTimestamp.toLocaleDateString('en-PH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })} ${transactionTimestamp.toLocaleTimeString('en-PH', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })}`,
  );
  const invoiceDisplay = getDisplayValue(
    state?.saleId,
    undefined,
    'Pending invoice number',
  );
  const paymentMethodDisplay = getDisplayValue(
    state?.paymentMethodName,
    undefined,
    'Payment method not provided',
  );

  const invoiceNote = state?.saleId?.startsWith('local_')
    ? 'Temporary reference until synced with BIR servers.'
    : 'Official invoice number.';

  const vatBreakdown = useMemo(() => {
    if (vatSnapshot) {
      return {
        vatableSales: vatSnapshot.vatableSales ?? 0,
        vatExemptSales: vatSnapshot.vatExemptSales ?? 0,
        vatZeroRatedSales: vatSnapshot.vatZeroRatedSales ?? 0,
        vatAmount: vatSnapshot.vatAmount ?? 0,
      };
    }

    if (!totalForVat) {
      return {
        vatableSales: 0,
        vatExemptSales: 0,
        vatZeroRatedSales: 0,
        vatAmount: 0,
      };
    }

    const vatableSales = Number((totalForVat / 1.12).toFixed(2));
    const vatAmount = Number((totalForVat - vatableSales).toFixed(2));

    return {
      vatableSales,
      vatExemptSales: 0,
      vatZeroRatedSales: 0,
      vatAmount,
    };
  }, [vatSnapshot, totalForVat]);

  const vatRows = [
    { label: 'Vatable Sales', value: vatBreakdown.vatableSales },
    { label: 'VAT-Exempt Sales', value: vatBreakdown.vatExemptSales },
    { label: 'VAT Zero-Rated Sales', value: vatBreakdown.vatZeroRatedSales },
    { label: 'VAT Amount (12%)', value: vatBreakdown.vatAmount },
  ];

  const handlePrint = useCallback(async () => {
    if (!state || !savedPrinter || !printerSettings.enabled) return;

    setIsPrinting(true);
    setPrintError(null);

    try {
      const receiptData: ReceiptData = {
        orderNumber: state.saleId,
        items:
          state.cart?.map((item) => ({
            name: item.productName,
            quantity: item.quantity,
            price: item.price,
          })) || [],
        total: state.total,
        paymentMethod: paymentMethodDisplay.text,
        amountReceived: cashAmount,
        change: changeAmount,
        customerName: customerInfo?.name,
        date: transactionTimestamp,
        shouldCut: printerSettings.autoCut,
      };

      const newDevice = await printReceipt(
        receiptData,
        savedPrinter,
        activeDevice,
      );
      if (newDevice) setActiveDevice(newDevice);
      setPrintError(null);
    } catch (err) {
      console.error('Print failed:', err);
      setPrintError((err as Error).message);
    } finally {
      setIsPrinting(false);
    }
  }, [
    state,
    savedPrinter,
    printerSettings,
    activeDevice,
    setActiveDevice,
    paymentMethodDisplay.text,
    cashAmount,
    changeAmount,
    transactionTimestamp,
    customerInfo,
  ]);

  // Auto-print on mount once company details are loaded
  useEffect(() => {
    if (
      state &&
      savedPrinter &&
      printerSettings.enabled &&
      !hasAutoPrintedRef.current
    ) {
      hasAutoPrintedRef.current = true;
      void handlePrint();
    }
  }, [state, savedPrinter, printerSettings.enabled, handlePrint]);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadCompany = async () => {
      try {
        const company = await companyService.getCurrentCompany();
        if (isMounted && company) {
          setCompanyDetails(company);
        }
      } catch (error) {
        if (!isMounted) return;
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Unable to load company information';
        setCompanyError(errorMessage);
      }
    };

    void loadCompany();

    return () => {
      isMounted = false;
    };
  }, [companyService]);

  // Check transaction sync status on mount and save as completed order
  useEffect(() => {
    if (!state?.isLocal || !state.saleId) return;

    const checkStatus = async () => {
      try {
        const transaction = await getPendingTransaction(state.saleId);
        if (transaction) {
          const currentStatus =
            transaction.syncStatus === 'synced' ? 'synced' : 'pending';
          setSyncStatus(currentStatus);
          await storeCompletedOrder(transaction, state.total, state.change);
        }
      } catch (error) {
        console.warn('Failed to check transaction status:', error);
      }
    };

    void checkStatus();
  }, [state]);

  // Attempt to sync transaction if it's local
  const attemptSync = useCallback(async () => {
    if (!state?.isLocal || !state.saleId) return;

    // Prevent multiple simultaneous sync attempts
    if (isSyncingRef.current) {
      return;
    }

    try {
      isSyncingRef.current = true;
      setSyncStatus('syncing');
      const transaction = await getPendingTransaction(state.saleId);
      if (!transaction) {
        setSyncStatus('error');
        isSyncingRef.current = false;
        return;
      }

      // Check if already synced before attempting sync
      if (transaction.syncStatus === 'synced' && transaction.serverId) {
        setSyncStatus('synced');
        isSyncingRef.current = false;
        return;
      }

      const result = await syncPendingTransaction(transaction, salesService);
      if (result.success) {
        setSyncStatus('synced');
        const updatedTransaction = await getPendingTransaction(state.saleId);
        if (updatedTransaction) {
          await storeCompletedOrder(
            updatedTransaction,
            state.total,
            state.change,
          );
        }
      } else {
        setSyncStatus('pending');
      }
    } catch {
      setSyncStatus('pending');
    } finally {
      isSyncingRef.current = false;
    }
  }, [state, salesService]);

  // Auto-sync when online and transaction is local and pending
  useEffect(() => {
    if (state?.isLocal && online && syncStatus === 'pending') {
      void attemptSync();
    }
  }, [state, online, syncStatus, attemptSync]);

  // Redirect if no state
  useEffect(() => {
    if (!state) {
      navigate(ROUTES.POS.ROOT, { replace: true });
    }
  }, [state, navigate]);

  const formatCurrency = useCallback((amount: number): string => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);
  }, []);

  const syncMessage = useMemo(() => {
    if (!state?.isLocal) return null;
    if (syncStatus === 'syncing') return 'Syncing to server...';
    if (syncStatus === 'synced') return 'Synced to server';
    if (syncStatus === 'pending') {
      return online
        ? 'Pending sync – will retry automatically'
        : 'Offline – will sync once online';
    }
    return 'Sync status unavailable';
  }, [state?.isLocal, syncStatus, online]);

  if (!state) {
    return null;
  }

  const formatCustomerField = (value?: string | null) => {
    if (!value || !value.trim()) {
      return '_______________';
    }
    return value;
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.controls}>
          <p className={styles.statusText}>*** TRANSACTION SUCCESSFUL ***</p>
          {syncMessage && <p className={styles.syncLine}>{syncMessage}</p>}
          <button
            type="button"
            className={styles.backButton}
            onClick={() => navigate(ROUTES.POS.ROOT)}
          >
            Return to POS
          </button>
          {printError && (
            <div className={styles.printErrorNote}>
              <p>Note: Printing failed. {printError}</p>
              <button
                type="button"
                className={styles.retryPrintBtn}
                onClick={() => void handlePrint()}
                disabled={isPrinting}
              >
                {isPrinting ? 'Printing...' : 'Retry Print'}
              </button>
            </div>
          )}
        </div>

        <div className={styles.receiptWrapper}>
          <div className={styles.receipt}>
            <p
              className={`${styles.centerText} ${styles.companyName} ${
                companyNameDisplay.isPlaceholder ? styles.placeholder : ''
              }`}
            >
              {companyNameDisplay.text}
            </p>
            <p
              className={`${styles.centerText} ${styles.companyDetail} ${
                companyAddressDisplay.isPlaceholder ? styles.placeholder : ''
              }`}
            >
              {companyAddressDisplay.text}
            </p>
            <p
              className={`${styles.centerText} ${
                tinDisplay.isPlaceholder ? styles.placeholder : ''
              }`}
            >
              {vatStatusLabel
                ? `${vatStatusLabel} TIN ${tinDisplay.text}`
                : `TIN: ${tinDisplay.text}`}
            </p>
            <p
              className={`${styles.centerText} ${
                posSerialDisplay.isPlaceholder ? styles.placeholder : ''
              }`}
            >
              POS SERIAL: {posSerialDisplay.text}
            </p>
            <p
              className={`${styles.centerText} ${
                machineIdDisplay.isPlaceholder ? styles.placeholder : ''
              }`}
            >
              MIN: {machineIdDisplay.text}
            </p>
            <p className={styles.invoiceLabel}>SALES INVOICE</p>

            <p className={styles.separator}>------------------------------</p>

            <div className={styles.receiptLine}>
              <span>Cashier</span>
              <span
                className={
                  cashierDisplay.isPlaceholder ? styles.placeholder : ''
                }
              >
                {cashierDisplay.text}
              </span>
            </div>
            <div className={styles.receiptLine}>
              <span>Date &amp; Time</span>
              <span
                className={
                  dateTimeDisplay.isPlaceholder ? styles.placeholder : ''
                }
              >
                {dateTimeDisplay.text}
              </span>
            </div>
            <div className={styles.receiptLine}>
              <span>Sales Invoice No.</span>
              <span
                className={
                  invoiceDisplay.isPlaceholder ? styles.placeholder : ''
                }
              >
                {invoiceDisplay.text}
              </span>
            </div>
            <p className={styles.centerText}>{invoiceNote}</p>

            <p className={styles.separator}>------------------------------</p>

            <div className={styles.receiptLine}>
              <span>Payment Method</span>
              <span
                className={
                  paymentMethodDisplay.isPlaceholder ? styles.placeholder : ''
                }
              >
                {paymentMethodDisplay.text}
              </span>
            </div>

            <p className={styles.sectionLabel}>Items</p>
            <div className={`${styles.tableRow} ${styles.tableHeader}`}>
              <span>Description</span>
              <span>Qty</span>
              <span>Price</span>
              <span>Amt</span>
            </div>
            {cartItems.length > 0 ? (
              cartItems.map((item) => {
                const unitPrice = Number(item.price ?? 0);
                const lineTotal = unitPrice * item.quantity;
                return (
                  <div key={item.productId} className={styles.tableRow}>
                    <span>{item.productName || 'Item'}</span>
                    <span>{item.quantity}</span>
                    <span>{formatCurrency(unitPrice)}</span>
                    <span>{formatCurrency(lineTotal)}</span>
                  </div>
                );
              })
            ) : (
              <div className={`${styles.tableRow} ${styles.placeholder}`}>
                <span>No order details captured</span>
                <span>-</span>
                <span>-</span>
                <span>-</span>
                <span>-</span>
              </div>
            )}

            <p className={styles.separator}>------------------------------</p>

            <div className={styles.receiptLine}>
              <span>Total Due</span>
              <span>{formatCurrency(subtotal)}</span>
            </div>
            <div className={styles.receiptLine}>
              <span>Cash</span>
              <span>{formatCurrency(cashAmount)}</span>
            </div>
            <div className={styles.receiptLine}>
              <span>Change</span>
              <span>{formatCurrency(Math.max(changeAmount, 0))}</span>
            </div>

            {shouldShowVatBreakdown && (
              <>
                <p className={styles.separator}>
                  ------------------------------
                </p>
                {vatRows.map((row) => {
                  const display = getDisplayValue(
                    typeof row.value === 'number' ? row.value : null,
                    (value) => formatCurrency(Number(value)),
                    formatCurrency(0),
                  );
                  return (
                    <div key={row.label} className={styles.receiptLine}>
                      <span>{row.label}</span>
                      <span
                        className={
                          display.isPlaceholder ? styles.placeholder : ''
                        }
                      >
                        {display.text}
                      </span>
                    </div>
                  );
                })}
              </>
            )}

            <p className={styles.separator}>------------------------------</p>

            <p className={styles.customerField}>
              Name: {formatCustomerField(customerInfo?.name)}
            </p>
            <p className={styles.customerField}>
              Address: {formatCustomerField(customerInfo?.address)}
            </p>
            <p className={styles.customerField}>
              TIN: {formatCustomerField(customerInfo?.tinNumber)}
            </p>
            <p className={styles.customerField}>
              Business Style: {formatCustomerField(customerInfo?.notes)}
            </p>

            <p className={styles.separator}>------------------------------</p>
            <p className={styles.centerText}>This serves as a sales invoice.</p>

            <div className={styles.centerText}>
              <p className={styles.providerCompact}>
                {POS_ACCREDITATION_DETAILS.PROVIDER_NAME} |{' '}
                {POS_ACCREDITATION_DETAILS.PROVIDER_ADDRESS}
              </p>
              <p className={styles.providerCompact}>
                {POS_ACCREDITATION_DETAILS.PROVIDER_TIN_STATUS} TIN{' '}
                {POS_ACCREDITATION_DETAILS.PROVIDER_TIN}
              </p>
              <p className={styles.providerCompact}>
                ACC#: {POS_ACCREDITATION_DETAILS.ACCREDITATION_NUMBER} | PTU:{' '}
                {POS_ACCREDITATION_DETAILS.PTU_NUMBER}
              </p>
              <p className={styles.providerCompact}>
                ACC Date: {POS_ACCREDITATION_DETAILS.ACCREDITATION_DATE_ISSUED}{' '}
                | Valid: {POS_ACCREDITATION_DETAILS.ACCREDITATION_VALID_UNTIL}
              </p>
              <p className={styles.providerCompact}>
                PTU Date: {POS_ACCREDITATION_DETAILS.PTU_DATE_ISSUED}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Success;
